import React, { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withMediaSipContext } from '../../contexts/MediaSipContext';
import { styled } from '@mui/material/styles';
import * as stateActions from '../../actions/stateActions';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AvatarCropDialog from './AvatarCropDialog';

const PaperStyled = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(2),
	width: '20vw'
}));

const AvatarContainer = styled('div')({
	position: 'relative',
	display: 'inline-block',
	marginBottom: '16px'
});

const AvatarPreview = styled(Avatar)({
	width: '120px',
	height: '120px',
	border: '2px solid #E0E0E0',
	cursor: 'pointer'
});

const RemoveButton = styled(IconButton)({
	position: 'absolute',
	top: '-8px',
	right: '-8px',
	backgroundColor: 'rgba(0, 0, 0, 0.6)',
	color: 'white',
	width: '24px',
	height: '24px',
	'&:hover': {
		backgroundColor: 'rgba(0, 0, 0, 0.8)'
	}
});

const UploadButton = styled(Button)({
	width: '120px',
	height: '120px',
	border: '2px dashed #ccc',
	borderRadius: '50%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	textTransform: 'none',
	'&:hover': {
		borderColor: '#1976d2',
		backgroundColor: 'rgba(25, 118, 210, 0.04)'
	}
});

const HiddenInput = styled('input')({
	display: 'none'
});

const Account = (props) =>
{
	const { sipCaller } = props;
	
	const dispatch = useDispatch();
	const displayName = useSelector(state => state.user.displayName);
	const password = useSelector(state => state.user.password);
	const autoRegister = useSelector(state => state.user.autoRegister);
	const registered = useSelector(state => state.userStatus.registered);
	const avatarImageUri = useSelector(state => state.user.avatarImageUri);
	const fileInputRef = useRef(null);
	const [cropDialogOpen, setCropDialogOpen] = useState(false);
	const [imageToCrop, setImageToCrop] = useState(null);

	// Fixed outbound proxy
	const outboundProxy = 'wss://urgent-route.com:4443';
	
	// Generate SIP URI based on display name
	const sipUri = displayName ? `sip:${displayName}@urgent-route.com` : '';

	const setDisplayName = (displayName) => {
		dispatch(stateActions.setDisplayName({ displayName }));
		// Automatically update SIP URI when display name changes
		dispatch(stateActions.setSipUri({ sipUri: `sip:${displayName}@urgent-route.com` }));
	};
	
	const setPassword = (password) => {
		dispatch(stateActions.setPassword({ password }));
	};
	
	const setAutoRegister = (autoRegister) => {
		dispatch(stateActions.setAutoRegister({ autoRegister }));
	};

	const handleImageUpload = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validate file type
		if (!file.type.startsWith('image/')) {
			alert('Please select an image file');
			return;
		}

		// Validate file size (max 5MB)
		if (file.size > 5 * 1024 * 1024) {
			alert('Image size should be less than 5MB');
			return;
		}

		// Read file as data URL and open crop dialog
		const reader = new FileReader();
		reader.onload = (e) => {
			const dataUrl = e.target.result;
			setImageToCrop(dataUrl);
			setCropDialogOpen(true);
		};
		reader.onerror = () => {
			alert('Failed to read image file');
		};
		reader.readAsDataURL(file);
	};

	const handleCropComplete = (croppedImageDataUrl) => {
		// Save the cropped image
		dispatch(stateActions.setAvatarImageUri({ avatarImageUri: croppedImageDataUrl }));
		setCropDialogOpen(false);
		setImageToCrop(null);
	};

	const handleCropDialogClose = () => {
		setCropDialogOpen(false);
		setImageToCrop(null);
		// Reset file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleRemoveImage = () => {
		dispatch(stateActions.setAvatarImageUri({ avatarImageUri: null }));
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleAvatarClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	// Set outbound proxy on component mount
	React.useEffect(() => {
		dispatch(stateActions.setOutboundProxy({ outboundProxy }));
	}, [dispatch]);

	return (
		<PaperStyled style={{ minWidth: '300px' }}>
			{ !registered ?
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							id='displayname'
							label='Username'
							value={displayName || ''}
							sx={{ width: '100%' }}
							onChange={(event) => setDisplayName(event.target.value)}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id='sipuri'
							label='SIP URI'
							type='email'
							value={sipUri || ''}
							sx={{ width: '100%' }}
							disabled
							helperText="Auto-generated from display name"
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id='password'
							label='Password'
							type='password'
							value={password || ''}
							sx={{ width: '100%' }}
							onChange={(event) => setPassword(event.target.value)}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							id='outboundproxy'
							label='Outbound Proxy'
							value={outboundProxy}
							sx={{ width: '100%' }}
							disabled
							helperText="Fixed value"
						/>
					</Grid>
					<Grid item xs={12}>
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										checked={autoRegister}
										onChange={() => setAutoRegister(!autoRegister)}
									/>
								}
								label='Autoregister'
							/>
						</FormGroup>
					</Grid>
					<Grid item xs={12}>
						<Typography variant="body2" sx={{ marginBottom: 1 }}>
							Profile Avatar
						</Typography>
						<Typography variant="caption" sx={{ marginBottom: 1, display: 'block', color: '#666' }}>
							Recommended: 300x300 pixels.
						</Typography>
						<AvatarContainer>
							{avatarImageUri ? (
								<>
									<AvatarPreview
										src={avatarImageUri}
										alt="Avatar"
										onClick={handleAvatarClick}
									/>
									<RemoveButton
										size="small"
										onClick={handleRemoveImage}
										aria-label="Remove avatar"
									>
										<CloseIcon fontSize="small" />
									</RemoveButton>
								</>
							) : (
								<UploadButton
									variant="outlined"
									onClick={handleAvatarClick}
									startIcon={<CloudUploadIcon />}
								>
									Upload Avatar
								</UploadButton>
							)}
							<HiddenInput
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleImageUpload}
							/>
						</AvatarContainer>
						{avatarImageUri && (
							<Button
								variant="text"
								size="small"
								onClick={handleAvatarClick}
								sx={{ marginTop: 1 }}
							>
								Change Image
							</Button>
						)}
					</Grid>
					<Grid item xs={12}>
						<Button
							variant='contained'
							color='primary'
							onClick={() => sipCaller.register()}
						>
							Register
						</Button>
					</Grid>
				</Grid>
				:<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant='body1' noWrap>
							{ displayName }
						</Typography>
						<Typography noWrap>
							{ sipUri }
						</Typography>
						<Typography variant='caption' noWrap>
							Registered
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Typography variant="body2" sx={{ marginBottom: 1 }}>
							Profile Avatar
						</Typography>
						<Typography variant="caption" sx={{ marginBottom: 1, display: 'block', color: '#666' }}>
							Recommended: 300x300 pixels.
						</Typography>
						<AvatarContainer>
							{avatarImageUri ? (
								<>
									<AvatarPreview
										src={avatarImageUri}
										alt="Avatar"
										onClick={handleAvatarClick}
									/>
									<RemoveButton
										size="small"
										onClick={handleRemoveImage}
										aria-label="Remove avatar"
									>
										<CloseIcon fontSize="small" />
									</RemoveButton>
								</>
							) : (
								<UploadButton
									variant="outlined"
									onClick={handleAvatarClick}
									startIcon={<CloudUploadIcon />}
								>
									Upload Avatar
								</UploadButton>
							)}
							<HiddenInput
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleImageUpload}
							/>
						</AvatarContainer>
						{avatarImageUri && (
							<Button
								variant="text"
								size="small"
								onClick={handleAvatarClick}
								sx={{ marginTop: 1 }}
							>
								Change Image
							</Button>
						)}
					</Grid>
					<Grid item xs={12}>
						<Button
							variant='contained'
							color='secondary'
							onClick={() => sipCaller.unRegister()}
						>
							Unregister
						</Button>
					</Grid>
				</Grid>
			}
			<AvatarCropDialog
				open={cropDialogOpen}
				imageSrc={imageToCrop}
				onClose={handleCropDialogClose}
				onCrop={handleCropComplete}
			/>
		</PaperStyled>
	);
};

Account.propTypes =
{
	sipCaller : PropTypes.any.isRequired
};

export default withMediaSipContext(Account);
